import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import { useCart } from '../../context/CartContext';
import { toast } from 'react-toastify';
import QuantityInput from '../../components/QuantityInput/QuantityInput';
import { FaImage, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { formatRupiah } from '../../utils/Formatter';
import './ProductDetailPage.css';

const ProductDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { addToCart } = useCart();

    const [barang, setBarang] = useState(null);
    const [productVariants, setProductVariants] = useState([]);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [loading, setLoading] = useState(true);
    const [currentImageIndex, setCurrentImageIndex] = useState(0); 
    const API_URL = api.defaults.baseURL.replace('/api', '');

    useEffect(() => {
        const fetchBarangDetail = async () => {
            if (!id) return;
            try {
                setLoading(true);
                const { data } = await api.get(`/barang/customer/${id}`);
                setBarang(data);
                const activeVariants = data.Products || [];
                setProductVariants(activeVariants);
                if (activeVariants.length > 0) {
                    setSelectedVariant(activeVariants[0]);
                }
                setCurrentImageIndex(data.BarangImages && data.BarangImages.length > 0 ? 0 : -1);
            } catch (error) {
                toast.error("Produk tidak ditemukan atau tidak aktif.");
                navigate('/');
            } finally {
                setLoading(false);
            }
        };
        fetchBarangDetail();
    }, [id, navigate]);

    const handlePrevImage = () => {
        if (!barang || !barang.BarangImages || barang.BarangImages.length === 0) return;
        setCurrentImageIndex(prevIndex => 
            prevIndex === 0 ? barang.BarangImages.length - 1 : prevIndex - 1
        );
    };

    const handleNextImage = () => {
        if (!barang || !barang.BarangImages || barang.BarangImages.length === 0) return;
        setCurrentImageIndex(prevIndex => 
            prevIndex === barang.BarangImages.length - 1 ? 0 : prevIndex + 1
        );
    };

    const handleThumbnailClick = (index) => {
        setCurrentImageIndex(index);
    };

    const handleVariantChange = (e) => {
        const variantId = e.target.value;
        const variant = productVariants.find(v => v.id === variantId);
        setSelectedVariant(variant);
        setQuantity(1);
    };

    const handleAddToCart = () => {
        if (!selectedVariant) {
            toast.warn("Pilih satuan terlebih dahulu.");
            return;
        }
        addToCart(selectedVariant.id, quantity);
        toast.success(`${quantity} x ${barang.nama_barang} (${selectedVariant.nama_satuan}) ditambahkan!`);
    };

    if (loading) return <p className="page-loading-text">Memuat...</p>;
    if (!barang) return null;

    const itemName = barang.nama_barang;
    const brandName = barang.Merek?.nama_merek;
    const typeName = barang.Merek?.Jeni?.nama_jenis;
    const currentImage = barang.BarangImages?.[currentImageIndex];

    return (
        <div className="page-containers">
            <div className="breadcrumb">
                <Link to="/">Home</Link> / <span>{typeName || 'Jenis'}</span> / <span>{brandName || 'Merek'}</span>
            </div>
            <div className="tokped-layout-card">
                <div className="tokped-image-section">
                    <div className="main-image-slider-container">
                        {barang.BarangImages && barang.BarangImages.length > 1 && (
                            <button className="slider-nav-btn prev" onClick={handlePrevImage}><FaChevronLeft /></button>
                        )}
                        <div className="main-image-container">
                            {currentImage ? (
                                <img src={`${API_URL}/${currentImage.imageUrl}`} alt={itemName} className="main-image" />
                            ) : (
                                <div className="tokped-image-placeholder">
                                    <FaImage size={50} />
                                    <p>Tidak Ada Gambar</p>
                                </div>
                            )}
                        </div>
                        {barang.BarangImages && barang.BarangImages.length > 1 && (
                            <button className="slider-nav-btn next" onClick={handleNextImage}><FaChevronRight /></button>
                        )}
                    </div>
                    
                    {/* --- KEMBALI MENGGUNAKAN STRUKTUR DIV SEDERHANA --- */}
                    <div className="thumbnail-scroll-container">
                        {barang.BarangImages?.map((img, index) => (
                            <div 
                                key={img.id} 
                                className={`thumbnail-item ${currentImageIndex === index ? 'active' : ''}`}
                                onClick={() => handleThumbnailClick(index)}
                            >
                                <img src={`${API_URL}/${img.imageUrl}`} alt={`thumbnail ${index + 1}`} />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Bagian Info Produk dan Aksi tidak berubah */}
                <div className="tokped-info-section">
                    <h1>{itemName || 'Nama Produk Tidak Tersedia'}</h1>
                    <p className="detail-brand">Merek: <strong>{brandName || 'Tanpa Merek'}</strong></p>
                    <p className="tokped-price">{formatRupiah(selectedVariant?.price || 0)}</p>
                    <div className="info-divider"></div>
                    <div className="product-description">
                        <h3>Deskripsi Produk</h3>
                        <p>{barang.deskripsi || 'Tidak ada deskripsi untuk produk ini.'}</p>
                    </div>
                </div>
                <div className="tokped-action-section">
                    <div className="action-card">
                        <h4>Atur Varian dan Jumlah</h4>
                        <div className="form-group">
                            <label>Pilih Satuan</label>
                            <select onChange={handleVariantChange} value={selectedVariant?.id || ''} disabled={productVariants.length === 0}>
                                {productVariants.length > 0 ? productVariants.map(v => (
                                    <option key={v.id} value={v.id}>{v.nama_satuan}</option>
                                )) : <option>Stok habis</option>}
                            </select>
                        </div>
                        <div className="quantity-control">
                            <p>Jumlah</p>
                            <QuantityInput quantity={quantity} onChange={setQuantity} maxStock={999} />
                        </div>
                        <div className="info-divider"></div>
                        <div className="subtotal-section">
                            <p>Subtotal</p>
                            <p className="subtotal-price">{formatRupiah((selectedVariant?.price || 0) * quantity)}</p>
                        </div>
                        <button onClick={handleAddToCart} className="add-to-cart-btn" disabled={!selectedVariant}>
                            + Keranjang
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetailPage;